import React, { useState } from "react"
import * as Yup from "yup"
import { ErrorMessage, Field, Form, Formik } from "formik"
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import configs from "config"
import { useSelector, useDispatch } from "react-redux"
import { addNewOpenTab } from "store/actions"

const { Url, apiVersion } = configs.client

function CreateNewChat({ createChatModal, toggleCreateChatModal }) {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Get access token from Redux store
  const { access } = useSelector(state => ({
    access: state.Login.userAccess,
  }))

  const initialValues = {
    chatNumber: "",
  }

  const validationSchema = Yup.object().shape({
    chatNumber: Yup.string()
      .matches(
        /^\d{10,12}$/,
        "Chat number must be 10-12 digits and contain only numbers"
      )
      .required("Chat number is required"),
  })

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setIsLoading(true)
      setError(null)
      const { chatNumber } = values

      // Format the number with country code
      const formattedNumber = chatNumber

      // Call the verification endpoint with authorization header
      const response = await axios.get(
        `${Url}/${apiVersion}/chats/verify/${formattedNumber}`,
        {
          headers: {
            Authorization: `Bearer ${access}`,
          },
        }
      )

      // If verification is successful, navigate to the chat with the chatID from response
      if (response.data && response.data.data.chatID) {
        const chatId = response.data.data.chatID

        // Create a proper chat object to add to the open tabs
        const chatObject = {
          _id: chatId,
          client: formattedNumber,
          contactName: response.data.data.contactName || {
            name: formattedNumber,
          },
        }

        // Add the chat to open tabs in Redux
        dispatch(addNewOpenTab(chatObject))

        // Navigate to the chat
        navigate(`/chat/${chatId}`)
        toggleCreateChatModal()
        resetForm()
      } else {
        // Handle case where chatID is not in response
        setError("Unable to verify chat. Please try again.")
      }
    } catch (error) {
      console.error("Error verifying chat:", error)
      setError(
        error.response?.data?.message ||
          "Failed to verify chat. Please try again."
      )
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  const handleKeyPress = event => {
    const keyCode = event.keyCode || event.which
    const keyValue = String.fromCharCode(keyCode)
    const numericRegex = /^[0-9]+$/

    if (!numericRegex.test(keyValue) && keyCode !== 13) {
      event.preventDefault()
    }
  }

  return (
    <Modal
      isOpen={createChatModal}
      toggle={toggleCreateChatModal}
      onOpened={() => document.getElementById("chatNumber").focus()}
    >
      <ModalHeader toggle={toggleCreateChatModal}>
        Create a new chat
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ resetForm, touched, errors, isSubmitting }) => (
          <Form>
            <ModalBody>
              <div className="form-group">
                <label htmlFor="chatNumber">Chat Number</label>
                <Field
                  type="text"
                  id="chatNumber"
                  name="chatNumber"
                  className={`form-control ${
                    errors.chatNumber ? "is-invalid" : ""
                  }`}
                  placeholder="e.g., 2010... (EG), 9665... (SA)"
                  autoComplete="off"
                  onKeyPress={handleKeyPress}
                  disabled={isLoading}
                />

                {errors.chatNumber && (
                  <div className="text-danger">{errors.chatNumber}</div>
                )}

                {error && <div className="text-danger mt-1">{error}</div>}
              </div>
            </ModalBody>

            <ModalFooter style={{ borderTop: "0", paddingTop: "0" }}>
              <Button
                color="secondary"
                type="submit"
                style={{ padding: "0.1875rem 0.875rem" }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Verifying...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
              <Button
                color="secondary"
                outline
                onClick={() => {
                  toggleCreateChatModal()
                  resetForm()
                  setError(null)
                }}
                style={{ padding: "0.1875rem 0.875rem" }}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

export default CreateNewChat
